import type { Locale } from '@/lib/i18n/config'
import { getBookingHref } from '@/lib/pages/bookingPaths'
import { normalizeHttpsUrl } from '@/lib/urls/normalizeHttpsUrl'

export type ApartmentRentLinkTarget = {
  href: string
  isExternal: boolean
}

/** Default Salesforce floor-plan / booking widget used on the booking page. */
export const DEFAULT_SALESFORCE_BOOKING_IFRAME_URL =
  'https://crescocapitalgroup.my.salesforce-sites.com/NeonWoodFloorPlan'

/**
 * Resolves the iframe `src` for the booking page Salesforce widget.
 * Prefers the apartment CMS booking URL when present; otherwise the shared widget.
 * Only German (`de`) appends `lang=de`. All other locales default to English without a `lang` parameter
 * because the external Salesforce widget only supports German and English.
 */
export function resolveApartmentBookingIframeUrl(
  locale: Locale,
  bookingUrl?: string | null,
): string {
  const base = normalizeHttpsUrl(bookingUrl) ?? DEFAULT_SALESFORCE_BOOKING_IFRAME_URL
  const url = new URL(base)

  if (locale === 'de') {
    url.searchParams.set('lang', 'de')
  } else {
    url.searchParams.delete('lang')
  }

  return url.toString()
}

/**
 * Resolves the Check availability destination for an apartment.
 * A valid external booking URL opens that page directly. Otherwise the localized
 * booking page opens in a new tab, with `?apartment=` so the iframe can load the
 * Salesforce booking URL.
 */
export function resolveApartmentRentLink(
  locale: Locale,
  apartmentSlug?: string,
  externalBookingUrl?: string | null,
): ApartmentRentLinkTarget {
  const externalHref = normalizeHttpsUrl(externalBookingUrl)
  if (externalHref) {
    return { href: externalHref, isExternal: true }
  }

  return { href: getBookingHref(locale, apartmentSlug), isExternal: true }
}
